import React from "react";
import Select from "react-select";

/* ══════════════════════════════════════════════════════════════
   TEMPLATE TAB
   List of inspection templates + the Create/Edit/View modal.
   All state/handlers are owned by the parent (InspectionCharacteristics.js)
   and passed in as props — this component is purely presentational.
══════════════════════════════════════════════════════════════ */
function InspectionTemplateTab({
  styles, customSelectStyles, DeleteIcon, ddl,
  templateList, handleView, handleEdit, handleDeleteTemplate, handleOpenCreate,
  showModal, setShowModal, resetForm,
  isViewMode, isEditMode, formData, setFormData,
  filteredInspTypes, qualityConfigurations,
  selectedCharacteristics, setSelectedCharacteristics,
  charMeta, setCharMeta,
  activeViewTab, setActiveViewTab, viewCharacteristics,
  handleCharacteristicSelect, handleGroupSelect,
  allTestTypes, handleSave, handleUpdate,
}) {
  // Variable names must be distinct within a template. Count every ticked
  // parameter's variable (case-insensitive) so duplicates can be flagged live.
  const isFormulaRow = (k) => (charMeta[k]?.isFormula || "No") === "Yes";
  const variableCounts = {};
  const availableVariables = [];   // names offered in the formula dropdown
  Object.keys(selectedCharacteristics || {})
    .filter(k => !k.startsWith("GROUP__"))
    .forEach(k => {
      if (isFormulaRow(k)) return;   // formula rows have no variable name
      const raw = (charMeta[k]?.variable || "").trim();
      const v = raw.toLowerCase();
      if (v) {
        variableCounts[v] = (variableCounts[v] || 0) + 1;
        if (!availableVariables.some(x => x.toLowerCase() === v)) availableVariables.push(raw);
      }
    });
  const isDuplicateVariable = (key) => {
    if (!selectedCharacteristics?.[key] || isFormulaRow(key)) return false;
    const v = (charMeta[key]?.variable || "").trim().toLowerCase();
    return !!v && variableCounts[v] > 1;
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
        <button onClick={handleOpenCreate} style={{ background: "#021843", color: "#fff", border: "none", borderRadius: "4px", padding: "5px 16px", fontSize: "13px", fontWeight: 500, height: "32px", cursor: "pointer" }}>
          Create
        </button>
      </div>
      <table className="table" style={styles.erpTable}>
        <thead>
          <tr>
            <th style={styles.erpTh}>Template Number</th>
            <th style={styles.erpTh}>Product Type</th>
            <th style={styles.erpTh}>Inspection Test Type</th>
            <th style={{ ...styles.erpTh, textAlign: "right", width: "180px" }}></th>
          </tr>
        </thead>
        <tbody>
          {templateList.length > 0 ? templateList.map(item => (
            <tr key={item.id}>
              <td style={styles.erpTd}>{item.inspection_no}</td>
              <td style={styles.erpTd}>{item.product_type || "—"}</td>
              <td style={styles.erpTd}>{Array.isArray(item.inspection_type) ? item.inspection_type.join(", ") : (item.inspection_type || "—")}</td>
              <td style={styles.erpTd}></td>
              <td style={{ ...styles.erpTd, textAlign: "right", whiteSpace: "nowrap" }}>
                <button className="btn btn-outline-dark btn-sm me-2" onClick={() => handleView(item.inspection_no)}>View</button>
                <button className="btn btn-dark btn-sm me-2" onClick={() => handleEdit(item.inspection_no)}>Edit</button>
                <button style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, color: "#dc2626", cursor: "pointer", padding: "4px 7px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                  onClick={() => handleDeleteTemplate(item.inspection_no)} title="Delete"><DeleteIcon /></button>
              </td>
            </tr>
          )) : (
            <tr><td colSpan="5" className="text-center text-muted p-3">No Records Found</td></tr>
          )}
        </tbody>
      </table>

      {/* ═══════════════════════════════════════════════
          MODAL — CREATE / EDIT / VIEW TEMPLATE
      ═══════════════════════════════════════════════ */}
      {showModal && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h5 style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>
                {isViewMode ? "View Inspection Template" : isEditMode ? "Edit Inspection Template" : "Create Inspection Template"}
              </h5>
              <button style={styles.closeBtn} onClick={() => { setShowModal(false); resetForm(); }}>✖</button>
            </div>

            <div style={styles.modalBody}>
              {/* Row 1: Product Type + Inspection Test Type */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label style={styles.compactLabel}>Product Type</label>
                  <select className="form-select" disabled={isViewMode}
                    value={formData.productType}
                    onChange={e => {
                      setFormData(p => ({ ...p, productType: e.target.value, inspectionType: [] }));
                    }}>
                    <option value="">Select</option>
                    {(ddl.productTypes || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>

                {/* Inspection Test Type — shown for any product type that has types configured */}
                <div className="col-md-6">
                  <label style={styles.compactLabel}>Inspection Test Type</label>
                  {formData.productType && filteredInspTypes.length > 0 ? (
                    <>
                      <Select
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        isDisabled={isViewMode}
                        options={filteredInspTypes}
                        value={filteredInspTypes.filter(o => (formData.inspectionType || []).includes(o.value))}
                        onChange={selected =>
                          setFormData(p => ({
                            ...p,
                            inspectionType: (selected || []).map(o => o.value),
                          }))
                        }
                        placeholder="Select Inspection Test Type(s)..."
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={customSelectStyles}
                      />
                      {!isViewMode && (
                        <small style={{ color: "#94a3b8", fontSize: "11px" }}>
                          Multiple selections allowed
                        </small>
                      )}
                    </>
                  ) : (
                    <div style={{ fontSize: 13, color: "#94a3b8", padding: "9px 0", minHeight: 38 }}>
                      {formData.productType
                        ? "No inspection test types configured for this product type"
                        : "Select a Product Type first"}
                    </div>
                  )}
                </div>
              </div>

              {/* Row 2: Template Number */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <label style={styles.compactLabel}>Template Number</label>
                  <input type="text" className="form-control" disabled={isViewMode || isEditMode}
                    value={formData.inspectionNo}
                    onChange={e => setFormData(p => ({ ...p, inspectionNo: e.target.value }))}
                    placeholder="e.g. INSP/2024/001" />
                </div>
              </div>

              {/* ── View mode: tabs ── */}
              {isViewMode && (
                <div>
                  <div style={styles.tabs}>
                    {["particulars"].map(t => (
                      <button key={t} className={`btn ${activeViewTab === t ? "btn-secondary" : "btn-light"}`}
                        onClick={() => setActiveViewTab(t)} style={styles.compactTabBtn}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </button>
                    ))}
                  </div>
                  {activeViewTab === "particulars" && (
                    <table style={styles.characteristicsTable}>
                      <thead>
                        <tr>
                          <th style={styles.tableHeader}>Process</th>
                          <th style={styles.tableHeader}>Sub Process</th>
                          <th style={styles.tableHeader}>Parameters</th>
                          <th style={styles.tableHeader}>Variable</th>
                          <th style={styles.tableHeader}>UOM</th>
                          <th style={styles.tableHeader}>Test Type</th>
                          <th style={styles.tableHeader}>Type</th>
                          <th style={styles.tableHeader}>Formula</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewCharacteristics.map(([key, val]) => (
                          <tr key={key}>
                            <td style={styles.tableCell}>{val.processName}</td>
                            <td style={styles.tableCell}>{val.subProcessName || "—"}</td>
                            <td style={styles.tableCell}>{val.characteristic}</td>
                            <td style={styles.tableCell}>{(charMeta[key]?.isFormula || (val.isFormula ? "Yes" : "No")) === "Yes" ? "—" : (charMeta[key]?.variable || val.variable || "—")}</td>
                            <td style={styles.tableCellUom}>{val.uom || "—"}</td>
                            <td style={styles.tableCell}>{val.testType || "—"}</td>
                            <td style={styles.tableCell}>{val.type || "—"}</td>
                            <td style={styles.tableCell}>
                              {(charMeta[key]?.isFormula || (val.isFormula ? "Yes" : "No")) === "Yes"
                                ? <span style={{ fontFamily: "monospace", background: "#eef2ff", color: "#3730a3", padding: "2px 6px", borderRadius: 4 }}>ƒ = {charMeta[key]?.variable || val.formula || "—"}</span>
                                : "No"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* ── Edit / Create mode: characteristic selection ── */}
              {(isEditMode || (!isViewMode && !isEditMode)) && qualityConfigurations.length > 0 && (
                <div>
                  {qualityConfigurations.map(group => {
                    const groupKey = `GROUP__${group.process_name}`;
                    const isGroupSelected = !!selectedCharacteristics[groupKey];
                    const characteristics = group.characteristics || [];
                    return (
                      <div key={groupKey} style={styles.simpleGroupWrapper}>
                        <div style={styles.groupRow}>
                          <label style={styles.groupLabel}>
                            <input type="checkbox" checked={isGroupSelected}
                              onChange={() => handleGroupSelect(group.process_name, characteristics)} />
                            <div style={styles.processText}>{group.process_id} - {group.process_name}</div>
                          </label>
                        </div>
                        {isGroupSelected && (
                          <div style={styles.simpleCharacteristicsSection}>
                            <table style={styles.characteristicsTable}>
                              <thead>
                                <tr>
                                  <th style={{ ...styles.tableHeader, width: "50px" }}>
                                    <input type="checkbox"
                                      checked={characteristics.length > 0 && characteristics.every(item => !!selectedCharacteristics[`CHAR__${item.process_name}__${item.characteristic?.trim()}`])}
                                      onChange={e => {
                                        const checked = e.target.checked;
                                        setSelectedCharacteristics(prev => {
                                          const u = { ...prev };
                                          characteristics.forEach(item => {
                                            const k = `CHAR__${item.process_name}__${item.characteristic?.trim()}`;
                                            if (checked) u[k] = u[k] || {
                                              processName:    item.process_name,
                                              subProcessName: item.sub_process_name,
                                              characteristic: item.characteristic,
                                              uom:            item.uom || "",
                                              testType:       item.test_type || "",
                                              valueType:      item.value_type || "",
                                              sequence:       item.sequence ?? null,
                                            };
                                            else delete u[k];
                                          });
                                          return u;
                                        });
                                      }}
                                    />
                                  </th>
                                  <th style={styles.tableHeader}>Parameters</th>
                                  <th style={styles.tableHeader}>Variable</th>
                                  <th style={styles.tableHeader}>UOM</th>
                                  <th style={styles.tableHeader}>Test Type</th>
                                  <th style={styles.tableHeader}>Type</th>
                                  <th style={styles.tableHeader}>Formula</th>
                                </tr>
                              </thead>
                              <tbody>
                                {characteristics.map((item, index) => {
                                  const key = `CHAR__${item.process_name}__${item.characteristic?.trim()}`;
                                  const selected = selectedCharacteristics[key];
                                  return (
                                    <tr key={index}>
                                      <td style={styles.tableCellCheckbox}>
                                        <input type="checkbox" checked={!!selected} onChange={() => handleCharacteristicSelect(group, item)} />
                                      </td>
                                      <td style={styles.tableCell}>{item.characteristic}</td>
                                      <td style={styles.tableCell}>
                                        {isFormulaRow(key) ? (
                                          <div style={{ minWidth: 200 }}>
                                            <input
                                              type="text"
                                              className="form-control form-control-sm"
                                              style={{ fontSize: 12, fontFamily: "monospace" }}
                                              placeholder="Formula e.g. D1*D2/1000"
                                              value={charMeta[key]?.variable || ""}
                                              onChange={e => setCharMeta(prev => ({ ...prev, [key]: { ...prev[key], variable: e.target.value } }))}
                                            />
                                            <select
                                              className="form-select form-select-sm"
                                              style={{ fontSize: 12, marginTop: 4 }}
                                              value=""
                                              onChange={e => {
                                                const v = e.target.value;
                                                if (!v) return;
                                                setCharMeta(prev => {
                                                  const cur = prev[key]?.variable || "";
                                                  const sep = cur && !/[\s(+\-*/]$/.test(cur) ? " " : "";
                                                  return { ...prev, [key]: { ...prev[key], variable: cur + sep + v } };
                                                });
                                              }}
                                            >
                                              <option value="">{availableVariables.length ? "+ Insert variable" : "No variables defined yet"}</option>
                                              {availableVariables.map(v => <option key={v} value={v}>{v}</option>)}
                                            </select>
                                          </div>
                                        ) : (
                                          <>
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          style={{
                                            fontSize: 12, minWidth: 110,
                                            ...(isDuplicateVariable(key) ? { borderColor: "#dc2626", background: "#fef2f2" } : {}),
                                          }}
                                          placeholder="e.g. D1"
                                          title={isDuplicateVariable(key) ? "Variable must be unique within the template" : ""}
                                          value={charMeta[key]?.variable || ""}
                                          onChange={e => setCharMeta(prev => ({ ...prev, [key]: { ...prev[key], variable: e.target.value } }))}
                                        />
                                        {isDuplicateVariable(key) && (
                                          <div style={{ color: "#dc2626", fontSize: 11, marginTop: 2 }}>Duplicate variable</div>
                                        )}
                                      </>
                                        )}
                                      </td>
                                      <td style={styles.tableCellUom}>{item.uom || "—"}</td>
                                      <td style={styles.tableCell}>
                                        <select
                                          className="form-select form-select-sm"
                                          style={{ fontSize: 12, minWidth: 140 }}
                                          value={charMeta[key]?.testType || ""}
                                          onChange={e => setCharMeta(prev => ({ ...prev, [key]: { ...prev[key], testType: e.target.value } }))}
                                        >
                                          <option value="">Select</option>
                                          {allTestTypes.map(tt => <option key={tt.id} value={tt.name}>{tt.name}</option>)}
                                        </select>
                                      </td>
                                      <td style={styles.tableCell}>
                                        <select
                                          className="form-select form-select-sm"
                                          style={{ fontSize: 12, minWidth: 130 }}
                                          value={charMeta[key]?.type || ""}
                                          onChange={e => setCharMeta(prev => ({ ...prev, [key]: { ...prev[key], type: e.target.value } }))}
                                        >
                                          <option value="">Select</option>
                                          <option value="Text">Text</option>
                                          <option value="Integer">Integer</option>
                                          <option value="Min">Min</option>
                                          <option value="Max">Max</option>
                                          <option value="Range">Range</option>
                                        </select>
                                      </td>
                                      <td style={styles.tableCell}>
                                        <select
                                          className="form-select form-select-sm"
                                          style={{ fontSize: 12, minWidth: 90 }}
                                          value={charMeta[key]?.isFormula || "No"}
                                          onChange={e => {
                                            const val = e.target.value;
                                            setCharMeta(prev => ({
                                              ...prev,
                                              [key]: {
                                                ...prev[key],
                                                isFormula: val,
                                                variable: (prev[key]?.isFormula || "No") === val ? (prev[key]?.variable || "") : "",
                                              },
                                            }));
                                          }}
                                        >
                                          <option value="No">No</option>
                                          <option value="Yes">Yes</option>
                                        </select>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {(isEditMode || (!isViewMode && !isEditMode)) && qualityConfigurations.length === 0 && (
                <div style={{ textAlign: "center", color: "#94a3b8", padding: "40px 0", fontSize: 14 }}>
                  {formData.productType ? "Loading parameters..." : "Select a Product Type to load characteristics."}
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              {!isViewMode && (
                <button style={styles.saveBtn} onClick={isEditMode ? handleUpdate : handleSave}>
                  {isEditMode ? "Update" : "Save"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default InspectionTemplateTab;